import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as OrgNameIcon } from '../../assets/icons/team-name-icon.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/icons/arrow-right-icon.svg';
import './OrgCreation.css';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { UserContext, OrgContext, WorkspaceContext } from '../../objects/Context';
import axios from 'axios';
import { getPostAuthDestination } from '../../utils/oauthFlow';


const OrgCreation = () => {
    const { user, setUser } = useContext(UserContext);
    const { org, setOrg } = useContext(OrgContext);
    const { workspace, setWorkspace } = useContext(WorkspaceContext);
    const [orgName, setOrgName] = useState('');
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [userInvites, setUserInvites] = useState([]);
    const [showInvites, setShowInvites] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const navigate = useNavigate();

    useEffect(() => {
        const checkInvites = async () => {
            await getUserInvites();
            setLoading(false);
        };
        checkInvites();
    }, [user]);

    useEffect(() => {
        if (userInvites.length > 0) {
            setShowInvites(true);
        } else {
            setShowInvites(false);
        }
    }, [userInvites]);

    // useEffect(() => {
    //     if (org && org.id) {
    //         navigate('/');
    //     }
    // }, [org]);
    
    ////////////////////////// API CALLS //////////////////////////

    const getUserInvites = async () => {
        try {
          const invitesResponse = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/users/get-org-invites/${user.email}`, { withCredentials: true });
          if (invitesResponse.data.invites && invitesResponse.data.invites.length > 0) {
            setUserInvites(invitesResponse.data.invites);
          }
        } catch (invitesError) {
          console.error('Error checking user invites:', invitesError);
        }
    }

    const handleInviteAccepted = async (invite) => {
        const wcId = invite.orgId; 
        if (!wcId || !user.id || !user.email) return;
        try {
          const inviteAccepted = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/orgs/invite-accepted`, {
            orgId: wcId,
            userId: user.id,
            email: user.email,
          }, { withCredentials: true });
          console.log('Invite accepted.');
          setUser((currentUser) => ({
            ...currentUser,
            orgId: inviteAccepted.data.id,
            orgIds: [...(currentUser.orgIds || []), inviteAccepted.data.id],
            orgRole: inviteAccepted.data.orgRole,
            accessLevel: inviteAccepted.data.accessLevel,
          }));
          setOrg(inviteAccepted.data);
          navigate(getPostAuthDestination());
        } catch (err) {
          console.error('Error accepting invite:', err);
        }
    }

    // Handle org name submission
    const handleNewOrgSubmit = async (e) => {
        if (orgName === '') {
            setErrorMessage('Please enter an org name.');
            return;
        }
        setErrorMessage(''); // Clear any previous error message
        e.preventDefault();
        setLoading(true);
        try {
            // Create the org
            const orgResponse = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/orgs/create-org`,
                {
                name: orgName,
                userId: user.id,
                userRole: 'admin',
                },  
                { withCredentials: true }
            );
            setOrg(orgResponse.data.org);
            setUser((currentUser) => ({
                ...currentUser,
                orgId: orgResponse.data.org.id,
                orgIds: [...(currentUser.orgIds || []), orgResponse.data.org.id],
                orgRole: 'admin',
            }));
            setWorkspace(orgResponse.data.workspace);
            navigate(getPostAuthDestination());
        } catch (err) {
            console.error('Error setting up org:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSwitchModal = (modal) => {
        if (modal === 'invites') {
            setShowInvites(true);
        } else {
            setShowInvites(false);
        }
    }

    const signOut = async () => {
        try {
            await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`, {}, { withCredentials: true });
            setUser(null);
            setOrg(null);
            setWorkspace(null);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    }
    
    return (
        <div className="org-creation">
            {loading ? <LoadingScreen isLoading={loading} /> : (
                <div className="content">
                    <h2>{showInvites ? <>You have been invited to the <br /> following organizations!</> : 'Set Up Your Organization'}</h2>
                    {!showInvites && <p className="info-text">Provide a name for your organization.<br />You can change it anytime.</p>}
                    <div className="dots">
                        <div className="dot" />
                        <div className="dot" />
                        <div className="dot" />
                    </div>

                    {showInvites ? (
                        <>
                            {userInvites.map((invite) => (
                                <li key={invite.orgId} className="input-wrapper invite" onClick={() => handleInviteAccepted(invite)}>
                                    <OrgNameIcon className="icon" />
                                    <div type="text" className="input">
                                        {invite.orgName}
                                    </div>
                                    <RightArrowIcon className="icon" />
                                </li>
                            ))}
                            <p className='divider'> - or - </p>
                            <p className='switch-modal' onClick={() => handleSwitchModal('new')}>Set up a new organization</p>
                        </>
                    ) : (
                        <>
                            <div className="input-wrapper org">
                                <OrgNameIcon className="icon" />
                                <input
                                    type="text"
                                    className="input"
                                    placeholder="Organization name"
                                    value={orgName}
                                    onChange={(e) => setOrgName(e.target.value)}
                                    required
                                />
                            </div>
                            {userInvites.length > 0 ? <p className='divider new'> - or - </p> : null}
                            {userInvites.length > 0 ? <p className='switch-modal new' onClick={() => handleSwitchModal('invites')}>Join an existing organization</p> : null}
                            <button
                                type="submit"
                                className="submit-btn"
                                disabled={isSubmitting}
                                onClick={handleNewOrgSubmit}
                            >
                                {isSubmitting ? 'Processing...' : 'Continue'}
                            </button>
                        </>
                    )}
                    {errorMessage && <p className="error-message">{errorMessage}</p>}
                    <p className="sign-out" onClick={() => signOut()}>Sign out</p>
                </div>
            )}
        </div>
    );
};

export default OrgCreation;